import type {EntityType} from 'src/types/ats-entities';
import type {StandardEntity} from 'src/types/target-entity-types';
import type {TranslationFunction} from 'src/types/language-support';


export const JOB_MATCH_ADDON_ID = 'sense-job-match-row';
export const JOB_MATCH_LINK_TEXT = 'Configure Job Match';

export const JOB_MATCH_API_PATH = '/bff/api/v1/';
export const JOB_MATCH_QUERY_PARAMS_PATH = 'discover/job-match/query-params';
export const JOB_MATCH_SENSE_APP = 'engage';
export const JOB_MATCH_DEFAULT_RULES_PATH = 'j2-job-match-params';

export const STANDARD_ENTITY_CANDIDATE: StandardEntity = 'candidate';
export const FALLBACK_CANDIDATE_ENTITY_TYPE: EntityType = 'bh_candidate';

/** Persisted on job match row metadata; not job-filter rule keys. */
export const METADATA_EXCLUDED_KEYS = new Set([
  'maxResults',
  'fieldsToShow',
  'jobMatchType',
  'response_key',
  'job_match_not_found_params',
  'variableIdentifier',
  'placeholder',
]);

/**
 * `job_match_not_found_params.action` values for `job_match_fetch` task.
 */
export const JOB_MATCH_NOT_FOUND_ACTION = {
  SKIP_AND_END_WORKFLOW: 'skip_and_end_workflow',
  SEND_ALTERNATIVE_TEXT: 'send_alternative_text',
} as const;

export type JobMatchNotFoundActionValue =
  typeof JOB_MATCH_NOT_FOUND_ACTION[keyof typeof JOB_MATCH_NOT_FOUND_ACTION];

export const DEFAULT_JOB_MATCH_NOT_FOUND_ACTION =
  JOB_MATCH_NOT_FOUND_ACTION.SKIP_AND_END_WORKFLOW;

/** API shape for `params.job_match_not_found_params`. */
export type JobMatchNotFoundParamsApi = {
  action: JobMatchNotFoundActionValue;
  alternative_text: string | null;
};

/** UI state shape for job match not found params (camelCase). */
export type JobMatchNotFoundParams = {
  action: JobMatchNotFoundActionValue;
  alternativeText: string | null;
};

export const DEFAULT_JOB_MATCH_NOT_FOUND_PARAMS: JobMatchNotFoundParams = {
  action: DEFAULT_JOB_MATCH_NOT_FOUND_ACTION,
  alternativeText: null,
};

export function buildJobMatchNotFoundParams(
  action: JobMatchNotFoundActionValue,
  alternativeText: string,
): JobMatchNotFoundParamsApi {
  if (action === JOB_MATCH_NOT_FOUND_ACTION.SEND_ALTERNATIVE_TEXT) {
    const trimmed = alternativeText.trim();
    return {
      action,
      alternative_text: trimmed.length > 0 ? trimmed : null,
    };
  }
  return {
    action,
    alternative_text: null,
  };
}

/**
 * Restores UI state from any object containing `job_match_not_found_params`
 * (e.g. Beefree metadata or `job_match_fetch` task params).
 */
export function parseJobMatchNotFoundParams(
  source: Record<string, unknown> | null | undefined,
): JobMatchNotFoundParams {
  const notFoundParams = source?.job_match_not_found_params as
    | JobMatchNotFoundParamsApi
    | undefined;
  if (!notFoundParams || typeof notFoundParams !== 'object') {
    return DEFAULT_JOB_MATCH_NOT_FOUND_PARAMS;
  }
  if (
    notFoundParams.action === JOB_MATCH_NOT_FOUND_ACTION.SEND_ALTERNATIVE_TEXT
  ) {
    return {
      action: JOB_MATCH_NOT_FOUND_ACTION.SEND_ALTERNATIVE_TEXT,
      alternativeText:
        typeof notFoundParams.alternative_text === 'string'
          ? notFoundParams.alternative_text
          : '',
    };
  }
  if (
    notFoundParams.action === JOB_MATCH_NOT_FOUND_ACTION.SKIP_AND_END_WORKFLOW
  ) {
    return {
      action: JOB_MATCH_NOT_FOUND_ACTION.SKIP_AND_END_WORKFLOW,
      alternativeText: null,
    };
  }
  return DEFAULT_JOB_MATCH_NOT_FOUND_PARAMS;
}

export const PANEL_STATES = {
  JOB_MATCHING: 'jobMatching',
  CUSTOMIZE: 'customize',
} as const;

export type PanelState = typeof PANEL_STATES[keyof typeof PANEL_STATES];

export const JOB_TITLE_FIELD_VALUE = 'job_title';

export const FIELDS_TO_SHOW_OPTIONS = [
  {label: 'Job Title', value: JOB_TITLE_FIELD_VALUE},
  {label: 'Zipcode', value: 'zipcode'},
  {label: 'City', value: 'city'},
  {label: 'State', value: 'state'},
  {label: 'Country', value: 'country'},
  {label: 'Company Name', value: 'company'},
  {label: 'Worksite', value: 'worksite_options'},
  {label: 'Employment Type', value: 'employment_types'},
  {label: 'Pay Rate', value: 'compensation'},
  {label: 'Contract Duration', value: 'contract_duration'},
  {label: 'Shifts', value: 'shifts'},
  {label: 'Disciplines', value: 'disciplines'},
  {label: 'Specialties', value: 'specialties'},
  {label: 'Certifications', value: 'certifications'},
] as const;

export const MAX_FIELDS_TO_SHOW = 5;

export const getJobMatchInfoBlockHTML = (t: TranslationFunction) => `
  <div
    contenteditable="false"
    data-locked="true"
    style="background:#E6F0FE;border:1px solid #EBEBEB;padding:20px;display:flex;gap:12px;align-items:flex-start;user-select:none;"
  >
    <div style="display:flex;flex-direction:column;gap:8px;">
      <div style="font-family: 'Centra No 2', sans-serif;font-size:16px;color:#17172A;">${t(
        'Matched_Jobs',
        'Matched Jobs',
      )}</div>

      <div style="font-family: 'Centra No 2', sans-serif;font-size:12px;color:#17172A;">${t(
        'Jobs_matching_the_candidate\'s_preferences_will_appear_below_in_an_easytoscan_table_format',
        'Jobs matching the candidate\'s preferences will appear below in an easy-to-scan table format.',
      )}</div>
    </div>
  </div>
`;
